import { useQuery } from '@tanstack/react-query'
import { api, Severity, type GridQuery } from '../api/client'
import type { Severity as SeverityValue } from '../api/client'
import './ComplianceSummary.css'

/**
 * Ordered most-urgent first, which is also the order the stacked bar reads in.
 * `tone` maps to the same severity colours the chips use, so a red segment here
 * and a red chip in the table mean the same thing.
 */
const BANDS = [
  { key: 'expired', label: 'expired', severity: Severity.Expired, tone: 'crit' },
  { key: 'unknown', label: 'unknown', severity: Severity.Unknown, tone: 'unk' },
  { key: 'dueSoon', label: 'soon', severity: Severity.DueSoon, tone: 'warn' },
  { key: 'valid', label: 'valid', severity: Severity.Valid, tone: 'ok' },
  { key: 'noExpiry', label: 'no expiry', severity: Severity.NoExpiry, tone: 'none' },
  { key: 'notApplicable', label: 'n/a', severity: Severity.NotApplicable, tone: 'na' },
] as const

export function ComplianceSummary({
  query,
  onFilter,
}: {
  query: GridQuery
  onFilter: (obligationTypeId: number, severity: SeverityValue) => void
}) {
  const summary = useQuery({
    // Keyed on the filters that change the counts — but NOT on page, because the
    // strip describes the whole fleet and must not flicker when paging.
    queryKey: ['compliance-summary', query.siteId, query.vehicleTypeId],
    queryFn: () => api.getComplianceSummary(query.siteId, query.vehicleTypeId),
    staleTime: 30_000,
  })

  if (summary.isPending) {
    return <div className="summary summary--loading">Loading summary…</div>
  }

  if (!summary.data?.length) return null

  return (
    <div className="summary">
      {summary.data.map((row) => {
        const total = row.total || 1
        const active =
          query.obligationTypeId === row.complianceTypeId ? query.obligationSeverity : undefined

        return (
          <section
            key={row.complianceTypeId}
            className={`sumcard ${active !== undefined ? 'sumcard--active' : ''}`}
          >
            <header className="sumcard__head">
              <span className="eyebrow">{row.displayName}</span>
              {/* The headline number is the EXPIRED count, because that is the
                  number that costs money and gets vehicles impounded. */}
              <button
                type="button"
                className={`sumcard__count ${row.expired ? 'sumcard__count--crit' : ''}`}
                title={`Show the ${row.expired} vehicles with expired ${row.displayName}`}
                onClick={() => onFilter(row.complianceTypeId!, Severity.Expired)}
              >
                {row.expired}
              </button>
            </header>

            <div
              className="sumbar"
              role="img"
              aria-label={`${row.expired} expired, ${row.dueSoon} due soon, ${row.valid} valid`}
            >
              {BANDS.map((band) => {
                const count = row[band.key] ?? 0
                if (count === 0) return null

                return (
                  <button
                    key={band.key}
                    type="button"
                    className={`sumbar__seg sumbar__seg--${band.tone} ${
                      active === band.severity ? 'sumbar__seg--on' : ''
                    }`}
                    style={{ width: `${(count / total) * 100}%` }}
                    title={`${count} ${band.label} — click to filter`}
                    aria-label={`${count} ${band.label}`}
                    onClick={() => onFilter(row.complianceTypeId!, band.severity)}
                  />
                )
              })}
            </div>

            <div className="sumlegend">
              {BANDS.map((band) => {
                const count = row[band.key] ?? 0
                // Expired is already the headline number; repeating it in the
                // legend wastes the space the other bands need.
                if (count === 0 || band.key === 'expired') return null

                return (
                  <button
                    key={band.key}
                    type="button"
                    className={`sumlegend__item ${active === band.severity ? 'sumlegend__item--on' : ''}`}
                    onClick={() => onFilter(row.complianceTypeId!, band.severity)}
                  >
                    <s className={`swatch swatch--${band.tone}`} />
                    {count} {band.label}
                  </button>
                )
              })}
            </div>
          </section>
        )
      })}
    </div>
  )
}
